import React, { useState, useEffect } from"react";
import { Plus, Calendar, DollarSign, Users, CheckCircle, Eye, FileDown, ArrowLeft } from"lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ConfirmDialog,
 ModernButton,
 Tabs,
 TabsList,
 TabsTrigger,
 TabsContent,
 ListPageSkeleton,
} from"../../components/ui";
import { DatePicker } from"../../components/form/DatePicker";
import { apiService } from"../../services/api";
import { Closure, Sale } from"../../types";
import { formatCurrency, formatDateToBR } from"../../utils/formatters";

export const ClosuresPage: React.FC = () => {
 const [closures, setClosures] = useState<Closure[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
 const [createDialogOpen, setCreateDialogOpen] = useState(false);
 const [closeDialogOpen, setCloseDialogOpen] = useState(false);
 const [selectedClosure, setSelectedClosure] = useState<Closure | null>(null);
 const [formData, setFormData] = useState({
 startDate:"",
 endDate:"",
 });
 const [selectedClosureDetails, setSelectedClosureDetails] = useState<Closure | null>(null);
 const [showDetails, setShowDetails] = useState(false);
 const [availableSales, setAvailableSales] = useState<Sale[]>([]);

 useEffect(() => {
 fetchClosures();
 }, []);

 const fetchClosures = async () => {
 try {
 setIsLoading(true);
 const response = await apiService.getClosures({ page: 1, limit: 50 });
 setClosures(response.closures || []);
 } catch (err) {
 console.error("Erro ao carregar fechamentos:", err);
 setToast({
 message:"Erro ao carregar fechamentos",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const fetchAvailableSales = async () => {
 try {
 const response = await apiService.getSales({ page: 1, limit: 100 });
 // Filter sales that are not in any closure
 const salesInClosures = new Set(
 closures.flatMap((c) => c.sales?.map((cs) => cs.saleId) || [])
 );
 const available = (response.sales || []).filter(
 (sale: Sale) => !salesInClosures.has(sale.id)
 );
 setAvailableSales(available);
 } catch (err) {
 console.error("Erro ao carregar vendas:", err);
 }
 };

 const handleCreate = async () => {
 if (!formData.startDate || !formData.endDate) {
 setToast({
 message:"Preencha as datas de início e fim",
 type:"error",
 });
 return;
 }

 try {
 await apiService.createClosure(formData);
 setToast({
 message:"Fechamento criado com sucesso!",
 type:"success",
 });
 setCreateDialogOpen(false);
 setFormData({ startDate:"", endDate:""});
 await fetchClosures();
 } catch (err) {
 console.error("Erro ao criar fechamento:", err);
 setToast({
 message:"Erro ao criar fechamento",
 type:"error",
 });
 }
 };

 const handleClose = async () => {
 if (!selectedClosure) return;

 try {
 await apiService.closeClosure(selectedClosure.id);
 setToast({
 message:"Fechamento processado com sucesso!",
 type:"success",
 });
 setCloseDialogOpen(false);
 setSelectedClosure(null);
 await fetchClosures();
 } catch (err) {
 console.error("Erro ao fechar fechamento:", err);
 setToast({
 message:"Erro ao fechar fechamento",
 type:"error",
 });
 }
 };

 const handleViewDetails = async (closureId: string) => {
 try {
 const closure = await apiService.getClosureById(closureId);
 setSelectedClosureDetails(closure);
 setShowDetails(true);
 } catch (err) {
 console.error("Erro ao carregar detalhes do fechamento:", err);
 setToast({
 message:"Erro ao carregar detalhes do fechamento",
 type:"error",
 });
 }
 };

 const handleExportReport = (closure: Closure) => {
 const reportData = {
 periodo: `${formatDateToBR(closure.startDate)} até ${formatDateToBR(closure.endDate)}`,
 status: closure.status ==="closed"?"Fechado":"Aberto",
 totalVendas: formatCurrency(parseFloat(closure.totalSales.toString())),
 totalComissoes: formatCurrency(parseFloat(closure.totalCommissions.toString())),
 totalPagamentos: formatCurrency(parseFloat(closure.totalPayments.toString())),
 vendas: closure.sales?.map((cs) => ({
 id: cs.sale.id,
 data: formatDateToBR(cs.sale.saleDate),
 cliente: cs.sale.customer?.name ||"N/A",
 veiculo: cs.sale.vehicle
 ? `${cs.sale.vehicle.brand?.name ||""} ${cs.sale.vehicle.model?.name ||""}`
 :"N/A",
 valor: formatCurrency(parseFloat(cs.sale.finalValue.toString())),
 })) || [],
 };

 const csvContent = [
"Relatório de Fechamento",
 `Período: ${reportData.periodo}`,
 `Status: ${reportData.status}`,
 `Total de Vendas: ${reportData.totalVendas}`,
 `Total de Comissões: ${reportData.totalComissoes}`,
 `Total de Pagamentos: ${reportData.totalPagamentos}`,
"",
"Vendas Incluídas",
"ID,Data,Cliente,Veículo,Valor",
 ...reportData.vendas.map((v) =>
 [v.id, v.data, v.cliente, v.veiculo, v.valor].map((cell) => `"${cell}"`).join(",")
 ),
 ].join("\n");

 const blob = new Blob([csvContent], { type:"text/csv;charset=utf-8;"});
 const link = document.createElement("a");
 const url = URL.createObjectURL(blob);
 link.setAttribute("href", url);
 link.setAttribute(
"download",
 `fechamento-${closure.id}-${new Date().toISOString().split("T")[0]}.csv`
 );
 link.style.visibility ="hidden";
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);

 setToast({
 message:"Relatório exportado com sucesso!",
 type:"success",
 });
 };

 if (showDetails && selectedClosureDetails) {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <ModernButton variant="outline" size="sm" onClick={() => { setShowDetails(false); setSelectedClosureDetails(null); }}>
 <ArrowLeft className="h-4 w-4 mr-1"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">Detalhes do Fechamento</h1>
 <p className="text-sm text-muted-foreground mt-0.5">
 {new Date(selectedClosureDetails.startDate).toLocaleDateString("pt-BR")} ate {new Date(selectedClosureDetails.endDate).toLocaleDateString("pt-BR")}
 </p>
 </div>
 </div>
 <Tabs defaultValue="sales"className="w-full">
 <TabsList className="grid w-full grid-cols-3">
 <TabsTrigger value="sales">Vendas</TabsTrigger>
 <TabsTrigger value="commissions">Comissoes</TabsTrigger>
 <TabsTrigger value="summary">Resumo</TabsTrigger>
 </TabsList>
 <TabsContent value="sales"className="space-y-4 mt-4">
 <Card>
 <CardHeader><CardTitle className="text-base">Vendas Incluidas</CardTitle></CardHeader>
 <CardContent>
 {selectedClosureDetails.sales && selectedClosureDetails.sales.length > 0 ? (
 <div className="space-y-2">
 {selectedClosureDetails.sales.map((cs) => {
 const sale = cs.sale;
 const finalValue = parseFloat(sale.finalValue.toString());
 return (
 <div key={cs.saleId} className="p-3 bg-surface rounded-lg">
 <div className="flex justify-between items-center">
 <div>
 <p className="font-medium">{sale.vehicle?.brand?.name ||""} {sale.vehicle?.model?.name ||""} {sale.vehicle?.modelYear ||""}</p>
 <p className="text-sm text-muted-foreground">Cliente: {sale.customer?.name ||"N/A"} | Data: {formatDateToBR(sale.saleDate)}</p>
 </div>
 <span className="font-semibold">{formatCurrency(finalValue)}</span>
 </div>
 </div>
 );
 })}
 </div>
 ) : (
 <p className="text-sm text-muted-foreground">Nenhuma venda incluida</p>
 )}
 </CardContent>
 </Card>
 </TabsContent>
 <TabsContent value="commissions"className="space-y-4 mt-4">
 <Card>
 <CardHeader><CardTitle className="text-base">Comissoes do Periodo</CardTitle></CardHeader>
 <CardContent>
 <div className="flex justify-between p-3 bg-surface rounded-lg">
 <span className="font-medium">Total de Comissoes:</span>
 <span className="font-semibold">{formatCurrency(parseFloat(selectedClosureDetails.totalCommissions.toString()))}</span>
 </div>
 </CardContent>
 </Card>
 </TabsContent>
 <TabsContent value="summary"className="space-y-4 mt-4">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Card><CardHeader><CardTitle className="text-base">Total de Vendas</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{formatCurrency(parseFloat(selectedClosureDetails.totalSales.toString()))}</p></CardContent></Card>
 <Card><CardHeader><CardTitle className="text-base">Total de Comissoes</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{formatCurrency(parseFloat(selectedClosureDetails.totalCommissions.toString()))}</p></CardContent></Card>
 <Card><CardHeader><CardTitle className="text-base">Total de Pagamentos</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{formatCurrency(parseFloat(selectedClosureDetails.totalPayments.toString()))}</p></CardContent></Card>
 </div>
 </TabsContent>
 </Tabs>
 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />}
 </div>
 );
 }

 return (
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <h1 className="text-2xl font-bold text-foreground font-display">
 Fechamentos
 </h1>
 <ModernButton
 onClick={() => {
 setFormData({ startDate:"", endDate:""});
 setCreateDialogOpen(true);
 }}
 >
 <Plus className="h-4 w-4 mr-2"/>
 Novo Fechamento
 </ModernButton>
 </div>

 {/* Create Dialog */}
 {createDialogOpen && (
 <Card>
 <CardHeader>
 <CardTitle>Criar Fechamento</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <DatePicker
 label="Data de Início"
 value={formData.startDate}
 onChange={(value) => setFormData({ ...formData, startDate: value })}
 required
 placeholder="Selecione a data de início"
 />
 <DatePicker
 label="Data de Fim"
 value={formData.endDate}
 onChange={(value) => setFormData({ ...formData, endDate: value })}
 required
 placeholder="Selecione a data de fim"
 />
 <div className="flex gap-2">
 <ModernButton onClick={handleCreate}>Criar</ModernButton>
 <ModernButton
 variant="outline"
 onClick={() => setCreateDialogOpen(false)}
 >
 Cancelar
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Closures List */}
 {isLoading ? (
 <ListPageSkeleton variant="cards" rows={6} showFilters={false} />
 ) : (
 <div className="grid gap-6">
 {closures.map((closure) => {
 const totalSales = parseFloat(closure.totalSales.toString());
 const totalCommissions = parseFloat(closure.totalCommissions.toString());
 const totalPayments = parseFloat(closure.totalPayments.toString());

 return (
 <Card key={closure.id}>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle className="flex items-center gap-2">
 <Calendar className="h-5 w-5"/>
 Fechamento
 {closure.status ==="closed"&& (
 <CheckCircle className="h-5 w-5 text-green-600"/>
 )}
 </CardTitle>
 <span
 className={`px-2 py-1 text-xs rounded-full ${
 closure.status ==="closed"
 ?"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
 :"bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
 }`}
 >
 {closure.status ==="closed"?"Fechado":"Aberto"}
 </span>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <p className="text-sm text-muted-foreground">Período</p>
 <p className="font-medium text-foreground">
 {new Date(closure.startDate).toLocaleDateString("pt-BR")} até{""}
 {new Date(closure.endDate).toLocaleDateString("pt-BR")}
 </p>
 </div>
 {closure.closedAt && (
 <div>
 <p className="text-sm text-muted-foreground">Fechado em</p>
 <p className="font-medium text-foreground">
 {new Date(closure.closedAt).toLocaleDateString("pt-BR")}
 </p>
 </div>
 )}
 </div>

 <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
 <div>
 <p className="text-sm text-muted-foreground flex items-center gap-1">
 <DollarSign className="h-4 w-4"/>
 Total de Vendas
 </p>
 <p className="text-lg font-bold text-foreground">
 {formatCurrency(totalSales)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground flex items-center gap-1">
 <Users className="h-4 w-4"/>
 Total de Comissões
 </p>
 <p className="text-lg font-bold text-foreground">
 {formatCurrency(totalCommissions)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground flex items-center gap-1">
 <DollarSign className="h-4 w-4"/>
 Total de Pagamentos
 </p>
 <p className="text-lg font-bold text-foreground">
 {formatCurrency(totalPayments)}
 </p>
 </div>
 </div>

 <div className="flex gap-2 pt-4 border-t border-border">
 <ModernButton
 variant="outline"
 onClick={() => handleViewDetails(closure.id)}
 >
 <Eye className="h-4 w-4 mr-2"/>
 Ver Detalhes
 </ModernButton>
 <ModernButton
 variant="outline"
 onClick={() => handleExportReport(closure)}
 >
 <FileDown className="h-4 w-4 mr-2"/>
 Exportar Relatório
 </ModernButton>
 {closure.status ==="open"&& (
 <>
 <ModernButton
 variant="outline"
 onClick={async () => {
 setSelectedClosure(closure);
 await fetchAvailableSales();
 }}
 >
 Adicionar Vendas
 </ModernButton>
 <ModernButton
 onClick={() => {
 setSelectedClosure(closure);
 setCloseDialogOpen(true);
 }}
 >
 Fechar Fechamento
 </ModernButton>
 </>
 )}
 </div>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>
 )}

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}

 <ConfirmDialog
 isOpen={closeDialogOpen}
 title="Fechar Fechamento"
 description="Tem certeza que deseja fechar este fechamento? Esta ação processará as comissões do período."
 onConfirm={handleClose}
 onClose={() => {
 setCloseDialogOpen(false);
 setSelectedClosure(null);
 }}
 type="warning"
 />
 </div>
 );
};

